import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import axios from 'axios'
import { useLanguage } from '../../context/LanguageContext'
import { trackAccountEmbed } from '../../utils/embedTrack'

/**
 * Customer-facing account-wide search iframe — Phase 9.
 * Mounted at /embed/reseller/:accountSlug/search.
 *
 * Faceted filter sidebar + result grid + live count. Each result links
 * to the per-grouping iframe (so the booking flow lands in the existing
 * Phase 7 path). Fully bilingual (EN/FR): opens in the account's default
 * language (data.reseller.defaultLanguage) with a header toggle, and package
 * name/description use their French variants (NameFr/DescriptionFr) in FR.
 */

// Pick the French variant of a content field when viewing in French (langId 2).
function pick(langId, en, fr) { return (langId === 2 && fr) ? fr : en }

export default function ResellerSearch() {
  const { accountSlug } = useParams()
  const { t, langId, setLangId } = useLanguage()
  // Use VITE_API_ORIGIN so the iframe reaches the API host instead of the
  // static SPA host (which returns index.html for /api/*).
  const api = useMemo(() => {
    const apiOrigin = import.meta.env.VITE_API_ORIGIN || window.location.origin
    return axios.create({ baseURL: `${apiOrigin}/api/public/reseller-search/${accountSlug}`, validateStatus: () => true })
  }, [accountSlug])

  const [filters, setFilters] = useState({ q: '', groupingId: '', minGolfers: '', minNights: '', maxNights: '', maxBudget: '' })
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)

  // Engagement: one 'view' per embed load; a debounced 'search' once the
  // visitor types a query. Fire-and-forget, attributed to the account.
  useEffect(() => { trackAccountEmbed(accountSlug, 'view', 'search') }, [accountSlug])
  useEffect(() => {
    const q = (filters.q || '').trim()
    if (q.length < 2) return
    const id = setTimeout(() => trackAccountEmbed(accountSlug, 'search', 'search', { q: q.slice(0, 80) }), 800)
    return () => clearTimeout(id)
  }, [filters.q, accountSlug])
  const [error, setError] = useState(null)
  const [appliedDefault, setAppliedDefault] = useState(false)
  // View mode: 'tile' (default — large image-led cards), 'grid' (compact
  // grid), 'list' (single-column rows with thumbnails). Persisted in
  // localStorage so the customer's choice sticks across page loads.
  const [view, setView] = useState(() => localStorage.getItem('rs_view') || 'tile')
  useEffect(() => { localStorage.setItem('rs_view', view) }, [view])

  // Responsive: below 720px (portrait phones) the filters/results grid stacks to
  // one column. Without this the fixed 260px sidebar squeezes the results column
  // so far that the min-280px cards overflow off-screen. window.innerWidth here
  // is the IFRAME's own width, so it reflects the embed's rendered size.
  const [narrow, setNarrow] = useState(() => typeof window !== 'undefined' && window.innerWidth < 720)
  useEffect(() => {
    const onResize = () => setNarrow(window.innerWidth < 720)
    onResize()
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])
  // On portrait phones the filters start COLLAPSED so packages are visible on
  // load (a full-height filter panel otherwise hides all results). Tap to open.
  const [filtersOpen, setFiltersOpen] = useState(false)

  useEffect(() => {
    setLoading(true)
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== ''))
    api.get('/', { params })
      .then(r => {
        if (r.status >= 400) { setError(r.data?.message || `Error ${r.status}`); setData(null) }
        else {
          setData(r.data); setError(null)
          // Funnel: how many packages this search surfaced. empty:true flags
          // a search that returned nothing (coverage/filter gap, not a UX drop).
          const n = Array.isArray(r.data?.results) ? r.data.results.length : 0
          trackAccountEmbed(accountSlug, 'results', 'search', { packages: n, empty: n === 0 })
        }
      })
      .finally(() => setLoading(false))
  }, [filters, api, accountSlug])

  // Open in the account's default language once; the header toggle overrides.
  useEffect(() => {
    if (!appliedDefault && data?.reseller?.defaultLanguage) {
      setLangId(data.reseller.defaultLanguage === 'fr' ? 2 : 1)
      setAppliedDefault(true)
    }
  }, [data, appliedDefault, setLangId])

  function setF(k, v) { setFilters(f => ({ ...f, [k]: v })) }

  const brand = data?.brand
  const primary = brand?.primaryColor || '#0f172a'

  if (error) return <Frame brand={brand}><div style={errorBox}>{error}</div></Frame>
  if (!data) return <Frame brand={brand}><div style={{ padding: 60, textAlign: 'center', color: '#64748b' }}>{t('loading_ellipsis', 'Loading…')}</div></Frame>

  const countKey = data.total === 1 ? 'sp_rsearch_count_one' : 'sp_rsearch_count_many'
  const activeFilterCount = Object.values(filters).filter(v => v !== '' && v != null).length

  return (
    <Frame brand={brand} reseller={data.reseller}>
      <h1 style={{ margin: '0 0 8px', fontSize: 26 }}>{t('sp_rsearch_title', 'Find your trip with {name}', { name: data.reseller.name })}</h1>
      <p style={{ color: '#64748b', marginTop: 0, marginBottom: 24, fontSize: 14 }}>
        {t('sp_rsearch_subtitle', "Search across all of {name}'s destinations. Apply filters; results refresh as you type.", { name: data.reseller.name })}
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: narrow ? '1fr' : '260px 1fr', gap: narrow ? 16 : 24 }}>
        {/* Sidebar — collapsible on portrait, always-open on wider screens */}
        {narrow ? (
          <div>
            <button type="button" onClick={() => setFiltersOpen(o => !o)}
              style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '11px 14px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, fontWeight: 600, color: '#0f172a', cursor: 'pointer', fontFamily: 'inherit' }}>
              <span>{t('sp_rsearch_filters', 'Filters')}{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}</span>
              <span style={{ color: '#64748b', fontSize: 12 }}>{filtersOpen ? '▲' : '▼'}</span>
            </button>
            {filtersOpen && <div style={{ marginTop: 12 }}><Filters filters={filters} setF={setF} facets={data.facets} /></div>}
          </div>
        ) : (
          <aside>
            <Filters filters={filters} setF={setF} facets={data.facets} />
          </aside>
        )}

        {/* Results */}
        <section>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div style={{ fontSize: 14, color: '#475569' }}>
              {loading ? '…' : t(countKey, '{count} packages found', { count: data.total })}
            </div>
            <ViewSwitcher view={view} setView={setView} primary={primary} />
          </div>
          {data.results.length === 0
            ? <div style={empty}>{t('sp_rsearch_empty', 'No packages match. Loosen the filters.')}</div>
            : <ResultsView results={data.results} view={view} primary={primary} />
          }
        </section>
      </div>
    </Frame>
  )
}

function Filters({ filters, setF, facets }) {
  const { t } = useLanguage()
  const allCount = Object.values(facets?.groupings || {}).reduce((s, g) => s + g.count, 0)
  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 16 }}>
      <Field label={t('sp_rsearch_f_search', 'Search')}>
        <input type="text" value={filters.q} onChange={e => setF('q', e.target.value)} placeholder={t('sp_rsearch_ph_search', 'Cape Breton, golf, …')} style={inp} />
      </Field>
      <Field label={t('sp_rsearch_f_destination', 'Destination')}>
        <select value={filters.groupingId} onChange={e => setF('groupingId', e.target.value)} style={inp}>
          <option value="">{t('sp_rsearch_all', 'All ({count})', { count: allCount })}</option>
          {Object.entries(facets?.groupings || {}).map(([id, g]) => (
            <option key={id} value={id}>{g.name} ({g.count})</option>
          ))}
        </select>
      </Field>
      <Field label={t('sp_rsearch_f_mingolfers', 'Min golfers')}>
        <input type="number" min={1} max={50} value={filters.minGolfers} onChange={e => setF('minGolfers', e.target.value)} style={inp} placeholder={t('sp_rsearch_ph_any', 'any')} />
      </Field>
      <Field label={t('sp_rsearch_f_nights', 'Nights')}>
        <div style={{ display: 'flex', gap: 6 }}>
          <input type="number" min={1} max={30} value={filters.minNights} onChange={e => setF('minNights', e.target.value)} style={{ ...inp, flex: 1 }} placeholder={t('sp_rsearch_ph_min', 'min')} />
          <input type="number" min={1} max={30} value={filters.maxNights} onChange={e => setF('maxNights', e.target.value)} style={{ ...inp, flex: 1 }} placeholder={t('sp_rsearch_ph_max', 'max')} />
        </div>
      </Field>
      <Field label={t('sp_rsearch_f_maxbudget', 'Max budget (rough)')}>
        <input type="number" step="100" value={filters.maxBudget} onChange={e => setF('maxBudget', e.target.value)} style={inp} placeholder={t('sp_rsearch_ph_budget', '$ total est.')} />
      </Field>
      <button onClick={() => setF('q', '') || setF('groupingId', '') || setF('minGolfers', '') || setF('minNights', '') || setF('maxNights', '') || setF('maxBudget', '')} style={{ marginTop: 8, width: '100%', padding: 8, background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>
        {t('sp_rsearch_clear', 'Clear filters')}
      </button>
    </div>
  )
}

function Frame({ brand, reseller, children }) {
  // Embedded: avoid 100vh — the parent auto-resizes the iframe to our content,
  // so a viewport-relative height feeds back and grows the iframe without bound.
  const isEmbedded = window !== window.parent
  return (
    <div style={{ minHeight: isEmbedded ? 0 : '100vh', background: '#f8fafc', fontFamily: 'system-ui, sans-serif' }}>
      <header style={{ background: '#fff', borderBottom: '1px solid #e2e8f0', padding: '14px 24px', display: 'flex', alignItems: 'center', gap: 16 }}>
        {brand?.logoUrl && <img src={brand.logoUrl} alt={reseller?.name || 'Brand'} style={{ height: 36, maxWidth: 180, objectFit: 'contain' }} />}
        {!brand?.logoUrl && reseller?.name && <strong style={{ fontSize: 18 }}>{reseller.name}</strong>}
        <div style={{ marginLeft: 'auto' }}><LangToggle /></div>
      </header>
      <main style={{ maxWidth: 1200, margin: '0 auto', padding: '32px 24px' }}>{children}</main>
      <footer style={{ padding: '24px', textAlign: 'center', fontSize: 12, color: '#94a3b8', borderTop: '1px solid #e2e8f0' }}>
        Powered by <a href="https://integolf.com" target="_blank" rel="noopener" style={{ color: '#00d19f', textDecoration: 'none', fontWeight: 600 }}>InteGolf</a>
      </footer>
    </div>
  )
}

// EN/FR language toggle for the search embed header (mirrors the grouping embed).
function LangToggle() {
  const { langId, setLangId } = useLanguage()
  const b = (id, label) => (
    <button type="button" onClick={() => setLangId(id)} style={{
      padding: '4px 10px',
      border: '1px solid #cbd5e1',
      borderLeft: id === 2 ? 'none' : '1px solid #cbd5e1',
      background: langId === id ? '#0f172a' : '#fff',
      color: langId === id ? '#fff' : '#475569',
      cursor: 'pointer', fontSize: 12, fontWeight: 600,
    }}>{label}</button>
  )
  return <div style={{ display: 'inline-flex', borderRadius: 6, overflow: 'hidden' }}>{b(1, 'EN')}{b(2, 'FR')}</div>
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#475569', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</label>
      {children}
    </div>
  )
}

// ─── View switcher + per-mode renderers ────────────────────────────────

function ViewSwitcher({ view, setView, primary }) {
  const { t } = useLanguage()
  const opts = [
    { v: 'tile', label: t('sp_rsearch_view_tile', 'Tile'), icon: '▣' },
    { v: 'grid', label: t('sp_rsearch_view_grid', 'Grid'), icon: '▦' },
    { v: 'list', label: t('sp_rsearch_view_list', 'List'), icon: '☰' },
  ]
  return (
    <div style={{ display: 'inline-flex', border: '1px solid #e2e8f0', borderRadius: 6, overflow: 'hidden' }}>
      {opts.map(o => (
        <button
          key={o.v}
          type="button"
          onClick={() => setView(o.v)}
          style={{
            padding: '6px 12px',
            background: view === o.v ? primary : '#fff',
            color: view === o.v ? '#fff' : '#475569',
            border: 'none',
            borderRight: o.v !== 'list' ? '1px solid #e2e8f0' : 'none',
            cursor: 'pointer',
            fontSize: 13,
            fontWeight: view === o.v ? 600 : 400,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
          title={t('sp_rsearch_view_suffix', '{label} view', { label: o.label })}
        >
          <span aria-hidden="true">{o.icon}</span>{o.label}
        </button>
      ))}
    </div>
  )
}

function ResultsView({ results, view, primary }) {
  if (view === 'list') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {results.map(r => <ListRow key={r.ID_NO} r={r} primary={primary} />)}
      </div>
    )
  }
  if (view === 'grid') {
    // Compact grid: smaller cards, more per row.
    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(200px, 100%), 1fr))', gap: 10 }}>
        {results.map(r => <CompactCard key={r.ID_NO} r={r} primary={primary} />)}
      </div>
    )
  }
  // Tile (default): image-led, larger.
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(280px, 100%), 1fr))', gap: 16 }}>
      {results.map(r => <TileCard key={r.ID_NO} r={r} primary={primary} />)}
    </div>
  )
}

function TileCard({ r, primary }) {
  const { langId } = useLanguage()
  const name = pick(langId, r.Name, r.NameFr)
  const desc = pick(langId, r.Description, r.DescriptionFr)
  return (
    <a href={`/embed/reseller/${r.GroupingIframeKey}#package-${r.Slug}`} target="_self" style={cardStyle()}>
      {r.HeroImageUrl
        ? <img src={r.HeroImageUrl} alt={name} style={{ width: '100%', height: 180, objectFit: 'cover' }} />
        : <div style={{ width: '100%', height: 180, background: primary, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 16, padding: 16, textAlign: 'center', fontWeight: 600 }}>{name}</div>}
      <div style={{ padding: 16 }}>
        <div style={badgeStyle}>{r.GroupingName}</div>
        <h3 style={{ margin: '0 0 8px', fontSize: 17, color: '#0f172a' }}>{name}</h3>
        {desc && <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 12px', lineHeight: 1.4 }}>{desc.slice(0, 110)}{desc.length > 110 ? '…' : ''}</p>}
        <Stats r={r} />
        <PriceBlock r={r} />
      </div>
    </a>
  )
}

function CompactCard({ r, primary }) {
  const { langId } = useLanguage()
  const name = pick(langId, r.Name, r.NameFr)
  return (
    <a href={`/embed/reseller/${r.GroupingIframeKey}#package-${r.Slug}`} target="_self" style={cardStyle()}>
      {r.HeroImageUrl
        ? <img src={r.HeroImageUrl} alt={name} style={{ width: '100%', height: 110, objectFit: 'cover' }} />
        : <div style={{ width: '100%', height: 110, background: primary, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 13, padding: 8, textAlign: 'center' }}>{name}</div>}
      <div style={{ padding: 10 }}>
        <div style={{ ...badgeStyle, fontSize: 9, marginBottom: 3 }}>{r.GroupingName}</div>
        <h3 style={{ margin: '0 0 6px', fontSize: 13, color: '#0f172a', lineHeight: 1.3 }}>{name}</h3>
        <Stats r={r} compact />
        <PriceBlock r={r} compact />
      </div>
    </a>
  )
}

function ListRow({ r, primary }) {
  const { t, langId } = useLanguage()
  const name = pick(langId, r.Name, r.NameFr)
  const desc = pick(langId, r.Description, r.DescriptionFr)
  return (
    <a href={`/embed/reseller/${r.GroupingIframeKey}#package-${r.Slug}`} target="_self" style={{
      ...cardStyle(),
      display: 'flex',
      alignItems: 'stretch',
    }}>
      <div style={{ flexShrink: 0, width: 140 }}>
        {r.HeroImageUrl
          ? <img src={r.HeroImageUrl} alt={name} style={{ width: 140, height: '100%', minHeight: 100, objectFit: 'cover' }} />
          : <div style={{ width: 140, height: '100%', minHeight: 100, background: primary, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 12, padding: 8, textAlign: 'center' }}>{name}</div>}
      </div>
      <div style={{ flex: 1, padding: 14, display: 'flex', flexDirection: 'column' }}>
        <div style={{ ...badgeStyle, marginBottom: 4 }}>{r.GroupingName}</div>
        <h3 style={{ margin: '0 0 6px', fontSize: 16, color: '#0f172a' }}>{name}</h3>
        {desc && <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 8px', lineHeight: 1.4 }}>{desc.slice(0, 160)}{desc.length > 160 ? '…' : ''}</p>}
        <div style={{ marginTop: 'auto', display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
          <Stats r={r} />
          {r.PriceTotal != null && (
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 10, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1 }}>{t('sp_rsearch_from', 'From')}</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: '#0f172a', lineHeight: 1 }}>
                {fmtMoney(r.PricePerPerson, { whole: true })}<span style={{ fontSize: 11, color: '#64748b', fontWeight: 400 }}> {t('sp_rsearch_per_golfer', '/ Golfer')}</span>
              </div>
              <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{t('sp_rsearch_total', '{amount} total', { amount: fmtMoney(r.PriceTotal) })}</div>
            </div>
          )}
        </div>
      </div>
    </a>
  )
}

function Stats({ r, compact }) {
  const { t } = useLanguage()
  // Use the larger of min/max as the "N golfers" label since the
  // package is sold for a fixed party (min usually equals max in v1).
  const golfers = r.MaxGolfers || r.MinGolfers || 1
  const golferLabel = r.MinGolfers === r.MaxGolfers
    ? t(golfers === 1 ? 'sp_rsearch_golfers_one' : 'sp_rsearch_golfers_many', '{count} Golfers', { count: golfers })
    : t('sp_rsearch_golfers_range', '{min}-{max} Golfers', { min: r.MinGolfers, max: r.MaxGolfers })
  // Combine golfers + rounds-each into one phrase per Todd's wording.
  const golfPhrase = r.RoundsPerGolfer
    ? golferLabel + t(r.RoundsPerGolfer === 1 ? 'sp_rsearch_rounds_each_one' : 'sp_rsearch_rounds_each_many', ' / {count} Rounds Each', { count: r.RoundsPerGolfer })
    : golferLabel
  const items = [
    { icon: '📅', text: t(r.Nights === 1 ? 'sp_rsearch_nights_one' : 'sp_rsearch_nights_many', '{count} Nights', { count: r.Nights }) },
    { icon: '👥', text: golfPhrase },
  ]
  if (r.CourseCount) items.push({ icon: '⛳', text: t(r.CourseCount === 1 ? 'sp_rsearch_courses_one' : 'sp_rsearch_courses_many', '{count} Available Courses', { count: r.CourseCount }) })
  return (
    <div style={{ display: 'flex', gap: compact ? 8 : 14, fontSize: compact ? 11 : 12, color: '#475569', flexWrap: 'wrap' }}>
      {items.map((it, i) => (
        <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <span aria-hidden="true">{it.icon}</span>{it.text}
        </span>
      ))}
    </div>
  )
}

function fmtMoney(value, { whole } = {}) {
  const n = Number(value)
  if (!Number.isFinite(n)) return ''
  const opts = whole
    ? { minimumFractionDigits: 0, maximumFractionDigits: 0 }
    : { minimumFractionDigits: 2, maximumFractionDigits: 2 }
  return '$' + n.toLocaleString('en-CA', opts)
}

function PriceBlock({ r, compact }) {
  const { t } = useLanguage()
  if (r.PriceTotal == null) return null
  const fontPer = compact ? 16 : 22
  return (
    <div style={{ marginTop: compact ? 8 : 10, paddingTop: compact ? 8 : 10, borderTop: '1px solid #f1f5f9' }}>
      <div style={{ fontSize: 10, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1 }}>{t('sp_rsearch_from', 'From')}</div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
        <strong style={{ fontSize: fontPer, color: '#0f172a' }}>{fmtMoney(r.PricePerPerson, { whole: true })}</strong>
        <span style={{ fontSize: 11, color: '#64748b' }}>{t('sp_rsearch_per_golfer', '/ Golfer')}</span>
      </div>
      <div style={{ fontSize: 11, color: '#64748b', marginTop: 1 }}>{t('sp_rsearch_total', '{amount} total', { amount: fmtMoney(r.PriceTotal) })}</div>
    </div>
  )
}

function cardStyle() {
  return {
    background: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: 8,
    overflow: 'hidden',
    textDecoration: 'none',
    color: 'inherit',
    display: 'block',
    transition: 'transform .12s, box-shadow .12s',
  }
}
const badgeStyle = { fontSize: 10, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 600 }

const inp = { width: '100%', padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box' }
const errorBox = { padding: 12, background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: 6 }
const empty = { padding: 60, textAlign: 'center', color: '#64748b', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8 }
